import React, { useState, useEffect } from "react";
import { Box, Typography, Button, IconButton, Paper, Chip, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { motion, AnimatePresence } from "framer-motion";

export interface BrowserInfo {
  isChrome: boolean;
  name: string;
}

export const detectBrowser = (): BrowserInfo => {
  if (typeof window === "undefined" || !navigator) {
    return { isChrome: true, name: "Unknown" };
  }

  const userAgent = navigator.userAgent || "";
  const vendor = navigator.vendor || "";

  // Check for Microsoft Edge
  if (/Edg\//i.test(userAgent)) {
    return { isChrome: false, name: "Microsoft Edge" };
  }

  // Check for Opera
  if (/OPR\/|Opera/i.test(userAgent)) {
    return { isChrome: false, name: "Opera" };
  }

  // Check for Firefox
  if (/Firefox|FxiOS/i.test(userAgent)) {
    return { isChrome: false, name: "Mozilla Firefox" };
  }

  // Check for Samsung Internet
  if (/SamsungBrowser/i.test(userAgent)) {
    return { isChrome: false, name: "Samsung Internet" };
  }

  // Check for iOS Chrome (CriOS)
  if (/CriOS/i.test(userAgent)) {
    return { isChrome: true, name: "Google Chrome" };
  }

  // Check for Safari (Apple Safari)
  const isSafari =
    /Safari/i.test(userAgent) &&
    /Apple Computer/i.test(vendor) &&
    !/Chrome/i.test(userAgent) &&
    !/CriOS/i.test(userAgent);

  if (isSafari) {
    return { isChrome: false, name: "Safari" };
  }

  // Standard Google Chrome
  const isGoogleChrome =
    /Chrome/i.test(userAgent) && (/Google Inc/i.test(vendor) || (window as any).chrome !== undefined);

  if (isGoogleChrome) {
    return { isChrome: true, name: "Google Chrome" };
  }

  if (/Chrome/i.test(userAgent)) {
    return { isChrome: true, name: "Chrome" };
  }

  return { isChrome: false, name: "trình duyệt hiện tại" };
};

const STORAGE_KEY = "fongfluency_dismiss_chrome_recommendation";

// Authentic Google Chrome Icon SVG
const ChromeIcon: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="12" fill="#FFFFFF" />
    <path d="M24 12H41.6C37.2 5.1 29.4 0.7 20.6 1.4C13.4 2 7 5.7 3.2 11.5L12 26.8L24 12Z" fill="#EA4335" />
    <path d="M24 12H41.6C45.6 18.2 45.6 26.2 41.5 32.4C38.3 37.3 33.1 40.5 27.2 41.7L18.4 26.5L24 12Z" fill="#FBBC05" />
    <path d="M27.2 41.7C20.4 43.1 13.2 41 8.3 36.1C3.4 31.2 1.3 24 2.7 17.2L17.5 31.9L27.2 41.7Z" fill="#34A853" />
    <circle cx="24" cy="24" r="8" fill="#1A73E8" />
  </svg>
);

export const BrowserRecommendationBanner: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [visible, setVisible] = useState<boolean>(false);
  const [browserName, setBrowserName] = useState<string>("");

  useEffect(() => {
    try {
      const isDismissed = localStorage.getItem(STORAGE_KEY);
      if (isDismissed === "true") {
        return;
      }
      const info = detectBrowser();
      if (!info.isChrome) {
        setBrowserName(info.name);
        setVisible(true);
      }
    } catch (e) {
      // LocalStorage access exception protection
      console.warn("Could not check browser recommendation status", e);
    }
  }, []);

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch (e) {
      console.warn("Could not save preference to localStorage", e);
    }
    setVisible(false);
  };

  const handleDownloadChrome = () => {
    window.open("https://www.google.com/chrome/", "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          style={{
            position: "fixed",
            bottom: "24px",
            left: "24px",
            zIndex: 99990,
            maxWidth: "420px",
            width: "calc(100vw - 48px)",
          }}
        >
          <Paper
            elevation={12}
            sx={{
              p: 2.5,
              borderRadius: "20px",
              position: "relative",
              overflow: "hidden",
              background: isDark
                ? "linear-gradient(135deg, rgba(15, 23, 42, 0.94) 0%, rgba(30, 41, 59, 0.94) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 252, 0.96) 100%)",
              backdropFilter: "blur(16px)",
              border: `1px solid ${isDark ? "rgba(59, 130, 246, 0.25)" : "rgba(59, 130, 246, 0.2)"}`,
              boxShadow: isDark
                ? "0 20px 40px -10px rgba(0, 0, 0, 0.6), 0 0 25px rgba(59, 130, 246, 0.15)"
                : "0 20px 40px -10px rgba(148, 163, 184, 0.25), 0 0 25px rgba(59, 130, 246, 0.12)",
            }}
          >
            {/* Ambient Background Glow Effect */}
            <Box
              sx={{
                position: "absolute",
                top: "-40px",
                right: "-40px",
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(234, 67, 53, 0) 70%)",
                pointerEvents: "none",
              }}
            />

            {/* Header section with Chrome Icon and Close button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    p: 0.75,
                    borderRadius: "14px",
                    bgcolor: isDark ? "rgba(255, 255, 255, 0.08)" : "#F1F5F9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "inset 0 1px 2px rgba(255,255,255,0.2)",
                  }}
                >
                  <ChromeIcon size={32} />
                </Box>
                <Box>
                  <Chip
                    icon={<WarningAmberRoundedIcon sx={{ fontSize: "14px !important", color: "#F59E0B" }} />}
                    label="Khuyến nghị trình duyệt"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor: isDark ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.12)",
                      color: isDark ? "#FBBF24" : "#D97706",
                      border: "1px solid rgba(245, 158, 11, 0.25)",
                      mb: 0.5,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.2,
                      color: isDark ? "#F8FAFC" : "#0F172A",
                    }}
                  >
                    Nên dùng Google Chrome
                  </Typography>
                </Box>
              </Box>

              <IconButton
                size="small"
                onClick={handleDismiss}
                aria-label="Đóng thông báo"
                sx={{
                  color: isDark ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.5)",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                    color: isDark ? "#fff" : "#000",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Description text */}
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#94A3B8" : "#475569",
                fontSize: "0.85rem",
                lineHeight: 1.5,
                mb: 2,
              }}
            >
              Hệ thống nhận thấy bạn đang truy cập bằng{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: isDark ? "#38BDF8" : "#0284C7",
                }}
              >
                {browserName}
              </Box>
              . Để các tính năng ghi âm và làm bài hoạt động chuẩn xác nhất, bạn nên chuyển sang dùng{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: isDark ? "#F8FAFC" : "#0F172A",
                }}
              >
                Google Chrome
              </Box>
              .
            </Typography>

            {/* Action buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                pt: 0.5,
              }}
            >
              <Button
                variant="contained"
                onClick={handleDownloadChrome}
                endIcon={<OpenInNewIcon sx={{ fontSize: "16px !important" }} />}
                sx={{
                  flex: 1,
                  borderRadius: "12px",
                  py: 1,
                  fontSize: "0.825rem",
                  fontWeight: 700,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                  boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
                    boxShadow: "0 6px 18px rgba(37, 99, 235, 0.45)",
                  },
                }}
              >
                Tải Google Chrome
              </Button>

              <Button
                variant="outlined"
                onClick={handleDismiss}
                sx={{
                  borderRadius: "12px",
                  py: 1,
                  px: 2,
                  fontSize: "0.825rem",
                  fontWeight: 700,
                  textTransform: "none",
                  color: isDark ? "#CBD5E1" : "#64748B",
                  borderColor: isDark ? "rgba(203, 213, 225, 0.2)" : "rgba(100, 116, 139, 0.3)",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                    borderColor: isDark ? "#CBD5E1" : "#475569",
                  },
                }}
              >
                Tôi đã hiểu
              </Button>
            </Box>
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BrowserRecommendationBanner;
